import type { ReactNode } from 'react'
import { Anchor, Group, Stack, Text } from '@mantine/core'
import { useNavigate } from 'react-router-dom'
import { SectionAction, SectionCard, Skeleton } from '@lighthouse/design'
import { useSignalSummary, useSignals } from '../../hooks/useSignals'
import {
  SignalEmptyState, SignalErrorState, SignalHandlingChip, SignalSeverity, SignalStateLabel,
} from '../signals/SignalBits'
import { detectorBanner, emptyView, signalTitle, sinceText } from '../signals/signalFormat'
import { exactNumber } from '../../utils/format'
import { queryView } from './overviewFormat'
import type { SignalListQuery } from '../../types/signals'

/** How many signals the preview lists. The query is the key by value, so it is a constant. */
const PREVIEW_QUERY: SignalListQuery = { state: 'open', take: 5 }

/**
 * The Signal Inbox, in five rows.
 *
 * The same states as the Inbox page, compact: loading, a failed request by status, a store not provisioned here,
 * a detector that never ran, one that has stopped reporting (its rows then read as unconfirmed), an empty list and
 * the rows. An empty list is an all-clear — with the check mark — ONLY under Complete coverage; under anything
 * less it says what was not observed and draws no check (signalFormat.emptyView).
 *
 * The list is asked only once the summary says there is a store and a detector: before the migration it would
 * answer 503, and before a first pass it has nothing to say.
 *
 * Each row carries the same handling chip as the Inbox (SignalHandlingChip): who has it — "Unowned", "Acknowledged ·
 * Alice" — under its state, never instead of it, because an acknowledged signal is still open. Where the handling
 * table does not exist the chip is a dash saying so, never "Unowned". The preview has no control of its own.
 *
 * It used to render four fixture signals — one a critical owned by a real person — as if they were live. The
 * Governance Queue and Report Registry previews that lived here were fixtures too; their live, admin-gated
 * replacements are components/governance/GovernanceQueuePreview and components/registry/ReportRegistryPreview.
 */
export function SignalInboxPreview() {
  const navigate = useNavigate()
  const summaryQuery = useSignalSummary()
  const view = queryView(summaryQuery)
  const summary = view === 'ready' ? summaryQuery.data! : null
  const listable = summary !== null && summary.storeProvisioned && !summary.detector.hasNeverRun
  const list = useSignals(PREVIEW_QUERY, { enabled: listable })
  const listView = queryView(list)
  const banner = summary ? detectorBanner(summary) : null

  let body: ReactNode
  if (view === 'error') body = <SignalErrorState error={summaryQuery.error} compact />
  else if (!summary) body = <PreviewSkeleton />
  else if (!listable) body = <BannerLine title={banner?.title ?? ''} message={banner?.message ?? ''} kind={banner?.kind ?? 'unavailable'} />
  else if (listView === 'error') body = <SignalErrorState error={list.error} compact />
  else if (listView === 'loading') body = <PreviewSkeleton />
  else {
    const data = list.data!
    body = (
      <Stack gap={8}>
        {banner && <BannerLine title={banner.title} message={banner.message} kind={banner.kind} />}
        {data.items.length === 0 ? (
          <SignalEmptyState view={emptyView(summary, false)} compact />
        ) : (
          <>
            {data.items.map((s) => (
              <div key={s.id} data-signal-row={s.id} style={{ display: 'flex', gap: 10, alignItems: 'flex-start', minWidth: 0 }}>
                <SignalSeverity severity={s.severity} />
                <Stack gap={0} style={{ minWidth: 0, flex: 1 }}>
                  <Anchor
                    component="button"
                    type="button"
                    size="sm"
                    fw={500}
                    ta="left"
                    onClick={() => navigate(`/signals?signal=${encodeURIComponent(s.id)}`)}
                  >
                    {signalTitle(s)}
                  </Anchor>
                  <Text size="xs" c="dimmed" style={{ overflowWrap: 'anywhere' }}>{s.subjectLabel} · {sinceText(s)}</Text>
                </Stack>
                <Stack gap={0} align="flex-end" style={{ flexShrink: 0 }}>
                  <SignalStateLabel signal={s} />
                  <SignalHandlingChip signal={s} />
                </Stack>
              </div>
            ))}
            {data.totalCount > data.items.length && (
              <Text size="xs" c="dimmed">{exactNumber(data.totalCount - data.items.length)} more open in the Inbox.</Text>
            )}
          </>
        )}
      </Stack>
    )
  }

  return (
    <SectionCard
      title="Signal Inbox"
      subtitle="Open signals, most severe first · not correlated: one failure can raise several"
      action={<SectionAction onClick={() => navigate('/signals')}>View all signals →</SectionAction>}
    >
      <div data-signal-preview={view === 'ready' ? (listable ? listView : banner?.kind ?? 'unavailable') : view}>{body}</div>
    </SectionCard>
  )
}

function PreviewSkeleton() {
  return (
    <Stack gap={8} aria-busy="true">
      <Skeleton height={14} width="80%" />
      <Skeleton height={14} width="70%" />
      <Skeleton height={14} width="60%" />
    </Stack>
  )
}

function BannerLine({ title, message, kind }: { title: string; message: string; kind: string }) {
  return (
    <Group gap={6} wrap="nowrap" align="flex-start" role="status" data-signal-detector={kind}>
      <Text size="xs" c={kind === 'stale' ? undefined : 'dimmed'} style={kind === 'stale' ? { color: 'var(--status-warning-fg)' } : undefined}>
        <strong>{title}.</strong> {message}
      </Text>
    </Group>
  )
}
